import { Box } from '@mui/material';
import { createContext, useContext, useEffect, type ReactElement } from 'react';
import { Link } from 'react-router';

import { targetPath, type ActiveNavEntry } from '../navigation';

/**
 * Fil d'Ariane de la barre superieure : « Rubrique / Entrée », derive du
 * registre, plus une feuille quand une page de detail en pose une.
 *
 * Le segment de rubrique **n'est pas un lien** : une rubrique n'a pas d'ecran a
 * elle. L'entree est un lien des que l'on est en dessous d'elle (une fiche, une
 * collection) ; sur sa propre page, c'est la page courante (`aria-current`).
 */

/** Poseur de feuille, fourni par la coque. */
const LeafSetterContext = createContext<(leaf: string | null) => void>(() => undefined);

export const BreadcrumbLeafProvider = LeafSetterContext.Provider;

/**
 * Pose la feuille du fil d'Ariane le temps que la page est montee.
 *
 * Le libelle doit **nommer la page, pas la donnee** (« Fiche du compte », pas
 * l'adresse) : il sert aussi au titre de l'onglet, qui finit dans l'historique
 * du navigateur.
 */
export const useBreadcrumbLeaf = (leaf: string): void => {
  const setLeaf = useContext(LeafSetterContext);

  useEffect(() => {
    setLeaf(leaf);

    return () => {
      setLeaf(null);
    };
  }, [leaf, setLeaf]);
};

const segmentSx = { fontSize: 13, fontWeight: 600, color: 'tokens.textMuted', whiteSpace: 'nowrap' } as const;

const Separator = (): ReactElement => (
  <Box component="li" aria-hidden sx={{ ...segmentSx, px: 0.75 }}>
    /
  </Box>
);

export const Breadcrumb = ({
  active,
  leaf,
}: {
  readonly active: ActiveNavEntry | null;
  readonly leaf: string | null;
}): ReactElement | null => {
  if (active === null && leaf === null) {
    return null;
  }

  const entryIsCurrent = active !== null && active.exact && leaf === null;

  return (
    <Box component="nav" aria-label="Fil d’Ariane" sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Box component="ol" sx={{ display: 'flex', alignItems: 'center', listStyle: 'none', m: 0, p: 0, minWidth: 0 }}>
        {active === null ? null : (
          <>
            <Box component="li" sx={segmentSx}>
              {active.section.label}
            </Box>
            <Separator />
            <Box component="li" sx={{ ...segmentSx, color: 'tokens.textStrong' }}>
              {entryIsCurrent ? (
                <span aria-current="page">{active.entry.label}</span>
              ) : (
                <Box
                  component={Link}
                  to={targetPath(active.entry.target)}
                  sx={{ color: 'inherit', textUnderlineOffset: 3, '&:focus-visible': { outline: '2px solid', outlineColor: 'tokens.primary', outlineOffset: 2, borderRadius: '4px' } }}
                >
                  {active.entry.label}
                </Box>
              )}
            </Box>
          </>
        )}
        {leaf === null ? null : (
          <>
            {active === null ? null : <Separator />}
            <Box
              component="li"
              aria-current="page"
              sx={{ ...segmentSx, color: 'tokens.textStrong', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {leaf}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
